// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/**
 * Score thresholds for interview scoring system
 * 0-20: Reject (Red)
 * 21-60: Hold (Amber)
 * 61-100: Advance (Green)
 */
export const SCORE_THRESHOLDS = {
  REJECT_MAX: 20,
  HOLD_MAX: 60,
  ADVANCE_MIN: 61
} as const;

/**
 * Score categories based on thresholds
 */
export type ScoreCategory = 'reject' | 'hold' | 'advance';

/**
 * Get the score category based on the score value
 * @param score - Score value (0-100)
 * @returns Score category ('reject' | 'hold' | 'advance')
 */
export function getScoreCategory(score: number): ScoreCategory {
  if (score <= SCORE_THRESHOLDS.REJECT_MAX) {
    return 'reject';
  }
  if (score <= SCORE_THRESHOLDS.HOLD_MAX) {
    return 'hold';
  }
  return 'advance';
}

/**
 * Get color for score category
 * @param score - Score value (0-100)
 * @returns Color code for the score
 */
export function getScoreColor(score: number): {
  primary: string;
  light: string;
  dark: string;
} {
  const category = getScoreCategory(score);

  const colors = {
    reject: {
      primary: '#dc3545',
      light: '#ff6b6b',
      dark: '#c82333'
    },
    hold: {
      primary: '#ff9800',
      light: '#ffc107',
      dark: '#e68900'
    },
    advance: {
      primary: '#5fcfac',
      light: '#7dd4b8',
      dark: '#2d9d78'
    }
  };

  return colors[category];
}

/**
 * Get recommendation badge color based on recommendation text
 * @param recommendation - Recommendation text ('Advance', 'Hold', 'Reject')
 * @returns Color configuration
 */
export function getRecommendationColor(recommendation: string): {
  bg: string;
  text: string;
  border: string;
} {
  const lowerRec = recommendation.toLowerCase();

  if (lowerRec.includes('reject')) {
    return {
      bg: 'rgba(220, 53, 69, 0.15)',
      text: '#dc3545',
      border: '#dc3545'
    };
  }

  if (lowerRec.includes('hold')) {
    return {
      bg: 'rgba(255, 152, 0, 0.15)',
      text: '#ff9800',
      border: '#ff9800'
    };
  }

  // Default to advance/green
  return {
    bg: 'rgba(95, 207, 172, 0.15)',
    text: '#5fcfac',
    border: '#5fcfac'
  };
}
